import { Box, Button, Tooltip, Typography, useTheme } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import React from 'react'

import thinkingSimba from '../../../../../shared/assets/simba-ai/Thinking.png'
import { useSimbaAIStore } from '../../../../store/simbaai.store'
import { Icons } from '../../../../../shared/utils/icons.jsx'
import { formatDate } from '../../../../../shared/utils/date-time-utilities.jsx'
import { useUserStore } from '../../../../store/user.store'

const SubjectPracticeSessions = ( { enterFullScreen } ) => {

    const { palette, layout, border, shadows } = useTheme()

    const SimbaAIStore = useSimbaAIStore()
    const UserStore = useUserStore()

    const [searchParams, setSearchParams] = useSearchParams()
    const selectedSubject = searchParams.get( 'selectedSubject' )

    let subjectDetails = UserStore.getUserSubjectsDetails.allSubjects?.find( sub => sub.subject_code === selectedSubject )
    if ( !subjectDetails ) {
        subjectDetails = SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject ).at( 0 )?.subject
        if ( !subjectDetails )
            subjectDetails = { subject_code: selectedSubject }
    }


    const setSearchParamsValues = ( values ) => {
        setSearchParams( prev => {
            const newParams = new URLSearchParams( prev )
            for ( const key in values ) {
                if ( !Object.hasOwn( values, key ) ) continue
                if ( values[key] )
                    newParams.set( key, values[key] )
                else
                    newParams.delete( key )
            }
            return newParams
        }, { replace: true } )
    }

    const backToHome = () => {
        setSearchParamsValues( { selectedSubject: "" } )
        SimbaAIStore.setView( "home", "right" )
    }

    const startQuiz = async ( practiceSession ) => {
        setSearchParamsValues( { practiceQuiz: practiceSession._id } )
        SimbaAIStore.setView( "loading" )
        const { success, data } = await SimbaAIStore.fetchPracticeSessionDetails( practiceSession._id )

        if ( success ) {
            if ( data.incomplete_session_exists ) {
                SimbaAIStore.setView( "continue-pending", "left", false )
            } else {
                if ( data.practiced ) {
                    SimbaAIStore.setView( "start-again", "left", false )
                } else {
                    const success = await SimbaAIStore.fetchPracticeSessionQuestions( practiceSession._id )
                    if ( success ) {
                        SimbaAIStore.setView( "question", "left", false )
                        enterFullScreen()
                    }
                }
            }
        }
    }

    return (
        <Box
            sx={{
                minHeight: 0,
                display: "flex",
                padding: "20px",
                flexGrow: 1,
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: "20px"
            }}>
            <Box
                sx={{
                    flexShrink: 0,
                    width: "100%",
                    padding: layout.pagePadding,
                    borderRadius: "10px",
                    gap: "20px",
                    alignItems: "center",
                    display: "flex",
                    background: palette.primary.light,
                    borderBottom: `4px solid ${palette.primary.light + "99"}`,
                    borderLeft: `4px solid ${palette.primary.light + "99"}`
                }}>
                <Box component="img" src={thinkingSimba} sx={{
                    height: "120px"
                }} />
                <Box>
                    <Typography
                        color={palette.common.white}
                        sx={{
                            lineHeight: 1,
                            fontSize: "28px",
                            fontWeight: 800
                        }}>{subjectDetails.subject_name || subjectDetails.subject_code}</Typography>
                    <Typography
                        color={palette.common.white}
                        sx={{
                            fontSize: "12px",
                            fontWeight: 500
                        }}>{SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject ).length} practice sessions available.</Typography>

                    <Box
                        sx={{
                            flexWrap: "wrap",
                            flexGrow: { xs: "1", sm: "initial" },
                            marginTop: "10px",
                            display: "flex",
                            gap: layout.cardsGap,
                            alignItems: "center"
                        }}>
                        <Button onClick={backToHome} sx={{ color: palette.common.white, border: `1px solid ${palette.common.white}`, borderBottom: `4px solid ${palette.common.white}`, borderRadius: "10px" }} startIcon={Icons.default.ArrowBack}  >Back to home</Button>
                    </Box>
                </Box>
            </Box>

            <Box
                sx={{
                    gap: layout.cardsGap,
                    display: "grid",

                    gridTemplateColumns: {
                        md: "repeat(4,1fr)",
                        sm: "repeat(2,1fr)",
                        sx: "repeat(1,1fr)",
                    }
                }}>
                {SimbaAIStore.getPracticeSessions.filter( ps => ps.subject.subject_code === selectedSubject ).map( practiceSession => {

                    const topics = Object.values( practiceSession.modules_and_topics ).map( mod => mod.topics ).flat()

                    return (
                        <Box
                            onClick={() => startQuiz( practiceSession )}
                            className="animation-wave"
                            key={practiceSession._id}
                            sx={{
                                flexGrow: { xs: 1, sm: "initial" },
                                overflow: "hidden",
                                display: "flex",
                                gap: "20px",
                                alignItems: "flex-start",
                                padding: "10px 20px",
                                borderRadius: "10px",
                                boxShadow: shadows[1],
                                border: border[1],
                                borderBottom: `4px solid ${palette.borderColor}`,
                                position: "relative",
                                bgcolor: palette.contentBg,
                                cursor: "pointer",
                                transition: "200ms",

                                ":hover": {
                                    borderColor: palette.primary.light,
                                    transform: `skew(-5deg)`
                                }
                            }}>
                            <Box
                                sx={{
                                    display: "flex",
                                    fontSize: "20px",
                                    color: palette.primary.contrastText,
                                    justifyContent: "center",
                                    width: "40px",
                                    height: "40px",
                                    borderRadius: "50%",
                                    bgcolor: palette.primary.dark,
                                    alignItems: "center",
                                    aspectRatio: "1 / 1"
                                }}>{Icons.default.QuizIconOutlined}</Box>
                            <Box
                                sx={{
                                    display: "flex",
                                    flexGrow: 1,
                                    overflow: "hidden",
                                    flexDirection: "column",
                                    alignItems: "flex-start"
                                }}>
                                <Typography
                                    color='textSecondary'
                                    sx={{
                                        fontWeight: 300,
                                        fontSize: "10px"
                                    }}>{formatDate( practiceSession.date_range_start )} to {formatDate( practiceSession.date_range_end )}</Typography>
                                <Tooltip disableInteractive title={practiceSession.title}>
                                    <Typography
                                        noWrap
                                        sx={{
                                            fontSize: "18px",
                                            width: "100%",
                                            overflow: "hidden",
                                            fontWeight: 600
                                        }}>{practiceSession.title}</Typography>
                                </Tooltip>
                                <Typography
                                    noWrap
                                    sx={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        fontSize: "12px"
                                    }}> {Icons.default.Sort2} {`Difficulty level: ${practiceSession.complexity}`}</Typography>
                                <Typography
                                    noWrap
                                    sx={{
                                        width: "100%",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        fontSize: "12px"
                                    }}>{Icons.default.User} {practiceSession.subject.subject_code} - {practiceSession.creator_info.name}</Typography>
                                <Box
                                    sx={{
                                        color: palette.text.secondary,
                                        display: "flex",
                                        gap: "5px",
                                        fontSize: "14px",
                                        alignItems: "center"
                                    }}>
                                    {topics.slice( 0, 1 ).map( ( topic, index ) => (
                                        <Typography
                                            title={topic}
                                            noWrap
                                            key={topic}
                                            color='textPrimary'
                                            sx={{
                                                maxWidth: "100px",
                                                fontSize: "12px",
                                                borderRadius: "5px",
                                                border: `1px solid ${palette.text.primary}`,
                                                bgcolor: palette.contentBg,
                                                padding: "2px 5px"
                                            }}>{topic}</Typography>
                                    ) )}
                                    {topics.length > 1 && <Typography
                                        color='textPrimary'
                                        sx={{
                                            fontSize: "12px",
                                            borderRadius: "5px",
                                            border: `1px solid ${palette.text.primary}`,
                                            bgcolor: palette.contentBg,
                                            padding: "2px 5px"
                                        }}>+{topics.length - 1} More</Typography>}
                                </Box>
                            </Box>
                        </Box>
                    );
                } )}
            </Box>

        </Box>
    );
}

export default SubjectPracticeSessions
